import Row from "react-bootstrap/Row";
import { useTranslation } from "react-i18next";
import withPolicyLinks from "../../utils/withPolicyLinks";
import s from "./styles.module.css";

// Renders the policy stored under `name` in translations: title, last-updated
// date, optional "intro" paragraphs and "summary" points, then "sections",
// each with paragraphs ("body"), a bullet list ("list") and paragraphs after
// the list ("after"). A section's "company" paragraph names the company and
// is shown only once its details are filled in under "company".
// Page-specific content goes in `children`, at the end.
export default function PolicyPage({ name, children }) {
    const { t, i18n } = useTranslation();
    const hasCompany = Boolean(t("company.name"));
    const list = (key) => {
        const value = t(key, { returnObjects: true });
        return Array.isArray(value) ? value : [];
    };
    const text = (value) => withPolicyLinks(value, i18n.language);

    const intro = list(`${name}.intro`);
    const summary = list(`${name}.summary`);
    const sections = list(`${name}.sections`);

    return (
        <>
            <Row>
                <h2 className="text-2xl text-center mt-5 underline break-words">
                    {t(`${name}.title`)}
                </h2>
                <p className="text-center text-sm text-[#6d6e71] mt-2 mb-4">
                    {t("policy.updated", { date: t(`${name}.date`) })}
                </p>
            </Row>

            {intro.length > 0 && (
                <Row>
                    {intro.map((paragraph) => (
                        <p key={paragraph} className="mb-2">{text(paragraph)}</p>
                    ))}
                </Row>
            )}

            {summary.length > 0 && (
                <Row>
                    {/* The Row's child gets the gutter; the gray box goes inside it. */}
                    <div>
                        <div className="rounded-2xl bg-[#f5f6f7] px-[20px] py-[16px]">
                            <p className="font-semibold">{t("policy.summary-title")}</p>
                            <ul className={`${s.summaryList} mt-2`}>
                                {summary.map((item) => (
                                    <li key={item} className="mb-1">{text(item)}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </Row>
            )}

            {sections.map(({ title, company, body = [], list = [], after = [] }) => (
                <Row key={title}>
                    <h3 className="text-xl mt-4 mb-2">{title}</h3>
                    {company && hasCompany && <p className="mb-2">{text(company)}</p>}
                    {body.map((paragraph) => (
                        <p key={paragraph} className="mb-2">{text(paragraph)}</p>
                    ))}
                    {list.length > 0 && (
                        <ul className={`${s.unorderedList} mb-2`}>
                            {list.map((item) => (
                                <li key={item} className="mb-1">{text(item)}</li>
                            ))}
                        </ul>
                    )}
                    {after.map((paragraph) => (
                        <p key={paragraph} className="mb-2">{text(paragraph)}</p>
                    ))}
                </Row>
            ))}

            {children}
        </>
    );
}
